import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { finalize, map, Observable, tap } from 'rxjs';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { BadgeComponent } from '../../../../../osp-ui/components/misc/badge/badge.component';
import { SpinnerComponent } from '../../../../../osp-ui/components/misc/spinner/spinner.component';
import { OverlayPanelComponent } from '../../../../../osp-ui/components/overlay/overlay-panel/overlay-panel.component';
import { ComponentSize } from '../../../../../osp-ui/enums/component-size.enum';
import { AttentionStatusTagComponent } from '../../../../../pages/resources/equipment/components/attention-status-tag/attention-status-tag.component';
import { EquipmentAttention } from '../../../../../pages/resources/equipment/contract/requests/equipment-attention.contract';
import { EquipmentCheckerService } from '../../../../../pages/resources/equipment/services/equipment-status-checker.service';
import { EquipmentsApiService } from '../../../../../pages/resources/equipment/services/equipments-api.service';
import { DynamicThumbnailComponent } from '../../../../../shared/components/dynamic-thumbnail/dynamic-thumbnail.component';
import { AddressPipe } from '../../../../../shared/pipes/address.pipe';
import { IconPipe } from '../../../../../shared/pipes/icon.pipe';

@Component({
  selector: 'osp-equipment-attention-overlay',
  standalone: true,
  imports: [
    RippleModule,
    OverlayPanelComponent,
    CommonModule,
    BadgeModule,
    BadgeComponent,
    TranslateModule,
    AddressPipe,
    DynamicThumbnailComponent,
    AttentionStatusTagComponent,
    IconPipe,
    SpinnerComponent,
  ],
  templateUrl: './equipment-attention-overlay.component.html',
  styleUrls: ['./equipment-attention-overlay.component.scss', '../topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EquipmentAttentionOverlayComponent implements OnInit {
  @ViewChild('op') overlayPanel!: OverlayPanelComponent;
  private equipmentApiService = inject(EquipmentsApiService);
  private equipmentCheckerService = inject(EquipmentCheckerService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  protected readonly ComponentSize = ComponentSize;
  protected readonly IconKey = IconKey;

  $attentionEquipments = signal<EquipmentAttention[]>([]);
  $attentionEquipmentsTotalAmount = signal(0);
  $isListLoading = signal(false);
  $isNoData = computed(() => this.$attentionEquipments().length && !this.$isListLoading());

  ngOnInit(): void {
    this.getAttentionEquipments();
  }

  protected getLinkToActiveEvents(equipment: EquipmentAttention): string | null {
    if (equipment.canSeeLinkToEvents) {
      return '/resources/equipment/' + equipment.equipmentId + '/events/active-logs';
    }
    return null;
  }

  private getAttentionEquipments(): void {
    this.$isListLoading.set(true);

    this.equipmentApiService
      .getAttentionEquipments()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ totalElements }) => this.$attentionEquipmentsTotalAmount.set(totalElements)),
        map(({ content }) => content),
        /*switchMap((equipments) =>
          forkJoin(equipments.map((equipment) => this.getAttentionEquipmentExtended(equipment))),
        ),*/
        finalize(() => this.$isListLoading.set(false)),
      )
      .subscribe((equipments) => this.$attentionEquipments.set(equipments));
  }

  public onItemClick(url: string): void {
    this.overlayPanel.hide();
    this.router.navigate([url]);
  }

  // TODO: Make adjustments on a backend side and then on the frontend (See details in the task FER-683)!
  // We are forced to make an additional request for EVERY(!!!) attention equipment
  // in order to get fields which are needed for "thumbnailKey" and "canSeeLinkToEvents" calculation
  // SOLUTION: Extend "/api/v2/equipments/search/attention-required" with fields:
  //    - thumbnailKey
  //    - status
  //    - hasLiveOperatingHours
  //    - hasLiveMileage
  //    - assignedTelematicsUnits
  //    - lastTelematicsUpdate
  private getAttentionEquipmentExtended(equipmentAttention: EquipmentAttention): Observable<EquipmentAttention> {
    return this.equipmentApiService.getEquipment(equipmentAttention.equipmentId).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((equipment) => ({
        ...equipmentAttention,
        thumbnailKey: equipment.thumbnailKey,
        canSeeLinkToEvents: this.equipmentCheckerService.canLogEquipmentEvents(equipment),
      })),
    );
  }
}
